/* global BigInt */
import { useCallback } from 'react';
import { encodeFunctionData, parseUnits } from 'viem';
import { useRecoilValue } from 'recoil';
import { signPreparedCalls } from '@account-kit/wallet-client';
import BalanceOfAbi from '../../utils/BalanceOfAbi.json';
import { usdr_token, usdt_token } from '../../utils/Enviroment';
import { userSmartAccount, userSmartAccountClient, userPublicClient } from '../../components/RecoilAtom';
import { useSessionPermissions } from '../checkSessionPermissions';
import { getTxHashAndReceipt, handleGasFeeWithPreparedCalls, sendGasFeeAsUsdt } from '../../utils/helpers';
import { USDT_MOCK_VALUE, USDR_DECIMALS, USDT_DECIMALS } from '../../utils/constants';
import { readConfirmedTransfer } from './WithdrawalAmount';
import {
    USDR_CONVERTER_ADDRESS,
    CONVERTER_USDT_ID,
    USDR_CONVERTER_ABI,
    IS_USDT_CONVERSION_ENABLED,
} from '../../utils/usdrConverter';

export class InsufficientUsdrError extends Error {
    constructor(available, requested) {
        super(`Insufficient USDR: have ${available}, need ${requested}`);
        this.name = 'InsufficientUsdrError';
    }
}

// Pay a USDR withdrawal out as USDT: the smart account approves the converter
// and calls buyStable in one session-key batch, with the network fee in USDR.
// buyStable takes USDT units (6 decimals) but burns the 18-decimal USDR
// equivalent, so the approval is scaled by the difference in decimals.
export const useConvertWithdraw = () => {
    const smartAccount = useRecoilValue(userSmartAccount);
    const smartAccountClient = useRecoilValue(userSmartAccountClient);
    const publicClient = useRecoilValue(userPublicClient);
    const { grantPermissions } = useSessionPermissions();

    const convertAndWithdraw = useCallback(
        async (toAddress, usdtAmount) => {
            if (!IS_USDT_CONVERSION_ENABLED) {
                return { txHash: null, receipt: null, confirmed: null, error: new Error('USDT withdrawals are not available right now') };
            }

            const stableAmt = parseUnits(usdtAmount.toString(), USDT_DECIMALS);
            const usdrAmount = stableAmt * 10n ** BigInt(USDR_DECIMALS - USDT_DECIMALS);

            try {
                const usdrBalance = await publicClient.readContract({
                    address: usdr_token,
                    abi: BalanceOfAbi,
                    functionName: 'balanceOf',
                    args: [smartAccount],
                });
                if (usdrBalance < usdrAmount) {
                    return {
                        txHash: null,
                        receipt: null,
                        confirmed: null,
                        error: new InsufficientUsdrError(
                            Number(usdrBalance) / 10 ** USDR_DECIMALS,
                            Number(usdrAmount) / 10 ** USDR_DECIMALS
                        ),
                    };
                }

                const approveCall = {
                    to: usdr_token,
                    data: encodeFunctionData({
                        abi: BalanceOfAbi,
                        functionName: 'approve',
                        args: [USDR_CONVERTER_ADDRESS, usdrAmount],
                    }),
                };
                const buyCall = {
                    to: USDR_CONVERTER_ADDRESS,
                    data: encodeFunctionData({
                        abi: USDR_CONVERTER_ABI,
                        functionName: 'buyStable',
                        args: [CONVERTER_USDT_ID, toAddress, stableAmt],
                    }),
                };
                // Estimate the whole batch in order: estimated alone, buyStable reverts on allowance
                const estimatedGasFee = await handleGasFeeWithPreparedCalls(smartAccountClient, smartAccount, [
                    approveCall,
                    buyCall,
                    { to: usdr_token, data: sendGasFeeAsUsdt(USDT_MOCK_VALUE) },
                ]);

                const { userPermissions: permissions, userSessionKey: sessionKey } = await grantPermissions();
                const preparedCalls = await smartAccountClient.prepareCalls({
                    calls: [
                        approveCall,
                        buyCall,
                        { to: usdr_token, data: sendGasFeeAsUsdt(Number(estimatedGasFee ?? 0)) },
                    ],
                    from: smartAccount,
                    capabilities: { permissions },
                });
                const signedCalls = await signPreparedCalls(sessionKey, preparedCalls);
                const sendResult = await smartAccountClient.sendPreparedCalls({
                    ...signedCalls,
                    capabilities: { permissions },
                });
                const { txHash, receipt } = await getTxHashAndReceipt({
                    preparedCallId: sendResult.preparedCallIds[0],
                    smartAccountClient,
                    publicClient,
                });
                const confirmed = readConfirmedTransfer(receipt, usdt_token, USDT_DECIMALS, toAddress);

                return {
                    txHash,
                    receipt,
                    confirmed,
                    error: confirmed ? null : new Error('Transaction reverted on-chain'),
                };
            } catch (error) {
                console.error('Convert withdraw error:', error);
                return { txHash: null, receipt: null, confirmed: null, error };
            }
        },
        [grantPermissions, publicClient, smartAccount, smartAccountClient]
    );

    return convertAndWithdraw;
};
